import React, { useEffect, useState } from "react";
import { FaTimesCircle } from "react-icons/fa";
import { useAuth } from "../../context/auth-context";
import { API_URL } from "../../constant";
import axiosInstance from "../../utils/axiosConfig";
import CopyToClipboard from "react-copy-to-clipboard";
import { formatAmount } from "../../utils/FormatPerson";
import { resolveBankNameFromLists } from "../../lib/api/bankNameResolver";

interface PayoutRecipient {
  accountName?: string;
  accountNumber?: string;
  bankCode?: string;
  bankName?: string;
}

interface PayoutUser {
  _id: string;
  email?: { address?: string };
  profile?: { firstName?: string; lastName?: string };
}

export interface PayoutDetail {
  _id: string;
  reference?: string;
  source?: string;
  provider?: string;
  recipient?: PayoutRecipient;
  transaction?: { type?: string; id?: string };
  batchId?: string;
  isAutomated?: boolean;
  createdAt?: string;
  updatedAt?: string;
  user?: PayoutUser;
}

type Props = {
  currency: string;
  currencyUSD: number;
  currencyNGN: number;
  status: string;
  date: string;
  payout?: PayoutDetail | null;
};
interface Bank {
  accountNumber: string;
  bankCode: string;
  accountName: string;
  isDefault: boolean;
  _id: string;
}

const CryptoPayoutConfirmation = ({
  currency,
  currencyUSD,
  currencyNGN,
  status,
  date,
  payout,
}: Props) => {
  const { setCryptoModal, payoutUserId, isAuthenticated } = useAuth();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");

  const [banks, setBanks] = useState<Bank | null>(null);
  const [copyMail, setCopyMail] = useState<boolean>(false);
  const [copyAccount, setCopyAccount] = useState<boolean>(false);
  const [copyAmount, setCopyAmount] = useState<boolean>(false);
  const [copyId, setCopyId] = useState<boolean>(false);
  const [copyReference, setCopyReference] = useState<boolean>(false);
  const [copyTransactionId, setCopyTransactionId] = useState<boolean>(false);
  const [copyBatchId, setCopyBatchId] = useState<boolean>(false);
  const [copyRecipientAccount, setCopyRecipientAccount] =
    useState<boolean>(false);
  const [allBanks, setAllBanks] = useState<any[]>([]);
  const [allOldBanks, setAllOldBanks] = useState<any[]>([]);

  const copyFeedbackMs = 2000;

  useEffect(() => {
    if (payout?.user) {
      setEmail(payout.user.email?.address ?? "");
      setFirstName(payout.user.profile?.firstName ?? "");
      setLastName(payout.user.profile?.lastName ?? "");
      return;
    }
    if (!payoutUserId) return;
    axiosInstance
      .get(`${API_URL}api/users/${payoutUserId}`)
      .then((r: any) => {
        const data = r.data?.data;
        if (data?.user) {
          setEmail(data.user.email?.address ?? "");
          setFirstName(data.user.profile?.firstName ?? "");
          setLastName(data.user.profile?.lastName ?? "");
        }
        const bankList: Bank[] = data?.user?.banks ?? [];
        const defaultBank = bankList.find((b: Bank) => b.isDefault) ?? null;
        setBanks(defaultBank);
      })
      .catch((e: any) => {
        console.log(e.response?.data?.message);
      });
  }, [isAuthenticated, payoutUserId, payout?.user]);

  useEffect(() => {
    Promise.all([
      axiosInstance.get(`${API_URL}api/banks/palmpay`),
      axiosInstance.get(`${API_URL}api/banks`),
    ])
      .then(([palmpayRes, oldRes]: any[]) => {
        setAllBanks(
          Array.isArray(palmpayRes?.data?.data) ? palmpayRes.data.data : []
        );
        setAllOldBanks(
          Array.isArray(oldRes?.data?.data) ? oldRes.data.data : []
        );
      })
      .catch((e: any) => {
        console.log(e?.response?.data?.message);
      });
  }, []);

  // console.log(payout);
  return (
    <div className="fixed inset-0 flex font-manrope justify-center items-start overflow-auto pt-16 pb-16  bg-black bg-opacity-40 backdrop-blur-sm">
      <div className="w-11/12 mds:w-10/12 lgss:w-2/5 transition-all p-6 ease-in duration-1000  rounded-xl bg-[#0D0D0D] ">
        <div className="flex  pt-4 justify-between items-center">
          <h4 className="font-bold text-[20px] font-sora text-white">
            Crypto Payout review
          </h4>
          <FaTimesCircle
            onClick={() => setCryptoModal((prev: any) => !prev)}
            className="text-white text-[32px] cursor-pointer"
          />
        </div>
        <div className="w-full flex-col mt-12 justify-center   items-center ">
          <h4 className="text-[42px] text-[#DEE1E6] text-center font-bold font-sora ">
            ${formatAmount(currencyUSD)}
          </h4>
          <h4 className="text-[22px] capitalize text-[#DEE1E6] text-center font-bold font-sora ">
            {firstName} {lastName}
          </h4>
        </div>

        <div className="w-full h-[1px] bg-[#AEBCC6] mt-12 mb-4" />
        {payout?._id && (
          <div className="w-full mt-2 flex justify-between items-center gap-2">
            <h4 className="text-[#AEBCC6] text-[14px] font-semibold shrink-0">
              Payout ID
            </h4>
            <CopyToClipboard
              text={payout._id}
              onCopy={() => {
                setCopyId(true);
                setTimeout(() => setCopyId(false), copyFeedbackMs);
              }}
            >
              <span className="text-[#0C84DA] text-[12px] xs:text-[14px] cursor-pointer font-mono truncate text-right block max-w-full">
                {copyId ? "Copied" : payout._id}
              </span>
            </CopyToClipboard>
          </div>
        )}
        <div className="w-full mt-3 flex justify-between items-center">
          <h4 className="text-[#AEBCC6] text-[14px] font-semibold">
            Digital Asset
          </h4>
          <h4 className="text-[#869AA9] text-[15px] uppercase ">{currency}</h4>
        </div>

        <div className="w-full mt-3 flex justify-between items-center">
          <h4 className="text-[#AEBCC6] text-[14px] font-semibold">
            Recipient address
          </h4>
          <CopyToClipboard
            text={email}
            onCopy={() => {
              setCopyMail(true);
              setTimeout(() => setCopyMail(false), copyFeedbackMs);
            }}
          >
            {copyMail ? (
              <h4 className="text-[12px] xs:text-[14px] text-[#869AA9]">
                Copied
              </h4>
            ) : (
              <h4 className="text-[#0C84DA] text-[14px] cursor-pointer ">
                {email}
              </h4>
            )}
          </CopyToClipboard>
        </div>
        <div className="w-full mt-3 flex justify-between items-center">
          <h4 className="text-[#AEBCC6] text-[14px] font-semibold">
            Amount paid
          </h4>
          <CopyToClipboard
            text={currencyNGN.toString()}
            onCopy={() => {
              setCopyAmount(true);
              setTimeout(() => setCopyAmount(false), copyFeedbackMs);
            }}
          >
            {copyAmount ? (
              <h4 className="text-[12px] xs:text-[14px] text-[#869AA9]">
                Copied
              </h4>
            ) : (
              <h4 className="text-[#869AA9] text-[15px] cursor-pointer ">
                ₦{formatAmount(currencyNGN)}
              </h4>
            )}
          </CopyToClipboard>
        </div>
        <div className="w-full mt-3 flex justify-between items-center">
          <h4 className="text-[#AEBCC6] text-[14px] font-semibold">
            Cash destination
          </h4>
          {payout?.recipient ? (
            <CopyToClipboard
              text={payout.recipient.accountNumber ?? ""}
              onCopy={() => {
                setCopyRecipientAccount(true);
                setTimeout(
                  () => setCopyRecipientAccount(false),
                  copyFeedbackMs
                );
              }}
            >
              <div className="text-right">
                <span className="text-[#0C84DA] cursor-pointer text-[13px] font-bold">
                  {copyRecipientAccount
                    ? "Copied"
                    : payout.recipient.accountNumber}
                </span>
                {payout.recipient.accountName && (
                  <span className="text-[#869AA9] text-[13px] block">
                    {payout.recipient.accountName}
                    {payout.recipient.bankName
                      ? ` · ${payout.recipient.bankName}`
                      : ""}
                  </span>
                )}
              </div>
            </CopyToClipboard>
          ) : (
            <CopyToClipboard
              text={banks ? banks?.accountNumber : ""}
              onCopy={() => {
                setCopyAccount(true);
                setTimeout(() => setCopyAccount(false), copyFeedbackMs);
              }}
            >
              {copyAccount ? (
                <h4 className="text-[12px] xs:text-[14px] text-[#869AA9]">
                  Copied
                </h4>
              ) : (
                <div className="text-[#0C84DA] cursor-pointer flex gap-2 justify-center items-center font-bold text-[13px] ">
                  {banks && banks.accountNumber}{" "}
                  <h5 className="text-[#869AA9]">
                    {resolveBankNameFromLists(
                      banks?.bankCode,
                      allBanks,
                      allOldBanks
                    )}
                  </h5>
                </div>
              )}
            </CopyToClipboard>
          )}
        </div>
        {payout?.reference && (
          <div className="w-full mt-3 flex justify-between items-center gap-2">
            <h4 className="text-[#AEBCC6] text-[14px] font-semibold shrink-0">
              Reference
            </h4>
            <CopyToClipboard
              text={payout.reference}
              onCopy={() => {
                setCopyReference(true);
                setTimeout(() => setCopyReference(false), copyFeedbackMs);
              }}
            >
              <span className="text-[#0C84DA] text-[12px] xs:text-[14px] cursor-pointer font-mono truncate text-right block">
                {copyReference ? "Copied" : payout.reference}
              </span>
            </CopyToClipboard>
          </div>
        )}
        {payout?.source && (
          <div className="w-full mt-3 flex justify-between items-center">
            <h4 className="text-[#AEBCC6] text-[14px] font-semibold">Source</h4>
            <h4 className="text-[#869AA9] text-[15px]">{payout.source}</h4>
          </div>
        )}
        {payout?.provider && (
          <div className="w-full mt-3 flex justify-between items-center">
            <h4 className="text-[#AEBCC6] text-[14px] font-semibold">
              Provider
            </h4>
            <h4 className="text-[#869AA9] text-[15px]">{payout.provider}</h4>
          </div>
        )}
        {payout?.transaction?.id && (
          <div className="w-full mt-3 flex justify-between items-center gap-2">
            <h4 className="text-[#AEBCC6] text-[14px] font-semibold shrink-0">
              Transaction ID
            </h4>
            <CopyToClipboard
              text={payout.transaction.id}
              onCopy={() => {
                setCopyTransactionId(true);
                setTimeout(() => setCopyTransactionId(false), copyFeedbackMs);
              }}
            >
              <span className="text-[#0C84DA] text-[12px] xs:text-[14px] cursor-pointer font-mono truncate text-right block">
                {copyTransactionId ? "Copied" : payout.transaction.id}
              </span>
            </CopyToClipboard>
          </div>
        )}
        {payout?.batchId && (
          <div className="w-full mt-3 flex justify-between items-center gap-2">
            <h4 className="text-[#AEBCC6] text-[14px] font-semibold shrink-0">
              Batch ID
            </h4>
            <CopyToClipboard
              text={payout.batchId}
              onCopy={() => {
                setCopyBatchId(true);
                setTimeout(() => setCopyBatchId(false), copyFeedbackMs);
              }}
            >
              <span className="text-[#0C84DA] text-[12px] xs:text-[14px] cursor-pointer font-mono truncate text-right block">
                {copyBatchId ? "Copied" : payout.batchId}
              </span>
            </CopyToClipboard>
          </div>
        )}
        {payout && typeof payout.isAutomated === "boolean" && (
          <div className="w-full mt-3 flex justify-between items-center">
            <h4 className="text-[#AEBCC6] text-[14px] font-semibold">
              Auto payout
            </h4>
            <h4 className="text-[#869AA9] text-[15px]">
              {payout.isAutomated ? "Yes" : "No"}
            </h4>
          </div>
        )}
        <div className="w-full mt-3 flex justify-between items-center">
          <h4 className="text-[#AEBCC6] text-[14px] font-semibold">Status</h4>
          <h4
            className={
              status === "PENDING"
                ? "text-[#CB9200] flex justify-center items-center w-[90px] bg-[#181818] tracking-wider h-[26px] rounded-full text-[11px] font-bold"
                : status === "IN PROGRESS"
                ? "text-[#60B7F6] flex justify-center items-center w-[90px] bg-[#181818] tracking-wider h-[26px] rounded-full text-[11px] font-bold"
                : status === "COMPLETED" || status === "SUCCESSFUL"
                ? "text-[#03C668] flex justify-center items-center w-[90px] bg-[#0E2B1F] tracking-wider h-[26px] rounded-full text-[11px] font-bold"
                : ""
            }
          >
            {status}
          </h4>
        </div>
        <div className="w-full mt-3 flex justify-between items-center">
          <h4 className="text-[#AEBCC6] text-[14px] font-semibold">Date</h4>
          <h4 className="text-[#869AA9] text-[15px] ">{date}</h4>
        </div>
        <div className="flex flex-col w-full gap-4 mt-8">
          <a
            href={`mailto:${email}`}
            className="flex justify-center items-center h-[47px] rounded-lg border bg-transparent border-[#0B84CB] text-white font-medium "
          >
            Contact the client
          </a>
        </div>
      </div>
    </div>
  );
};

export default CryptoPayoutConfirmation;
